/**
 * AdminAuditLog.tsx — globalny log zdarzeń admina (F7a T7.8).
 *
 * Widok: tabela Data | Admin | Akcja | Klient | Szczegóły. CAŁE filtrowanie żyje w
 * nagłówkach kolumn (client-side na pobranej liście): szukajka pod Admin/Akcja/Klient,
 * zakres dat od/do pod kolumną „Data" (bez osobnego paska nad tabelą). Nazwy akcji
 * humanizowane („Klient · zawieszono") z surowym kodem pod spodem (`humanizeAuditAction`).
 *
 * Data: `GET /api/admin/audit-log?limit=100`.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';
import { humanizeAuditAction } from '@/lib/humanize-audit-action';

/** Górny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

export interface AuditEntry {
  id: string;
  adminId: string | null;
  adminEmail: string | null;
  action: string;
  targetTenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  tenantCode: string | null;
  details: unknown;
  createdAt: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminAuditLog() {
  const { t } = useTranslation();

  const auditQuery = useQuery<AuditResponse>({
    queryKey: ['admin', 'auditLog'],
    queryFn: async () => {
      const res = await api.get<AuditResponse>(`/admin/audit-log?limit=${LIST_FETCH_LIMIT}`);
      return res.data;
    },
  });

  const columns = useMemo<ColumnDefinition<AuditEntry>[]>(
    () => [
      {
        id: 'createdAt',
        // Data-only (YYYY-MM-DD) do porównania zakresu; renderCell pokazuje datę z godziną.
        accessor: (row) => (row.createdAt ? row.createdAt.slice(0, 10) : ''),
        header: t('admin.auditLog.columns.createdAt'),
        sortable: true,
        // Zakres dat od/do pod kolumną (zamiast osobnego paska nad tabelą).
        headerFilter: { category: 'date' },
        renderCell: (_value, row) => (
          <span className="font-mono text-xs text-text-secondary">
            {formatDateTime(row.createdAt)}
          </span>
        ),
      },
      {
        id: 'admin',
        accessor: (row) => row.adminEmail ?? '',
        header: t('admin.auditLog.columns.admin'),
        sortable: true,
        renderCell: (_value, row) =>
          row.adminEmail ? (
            <span className="text-sm text-text-primary">{row.adminEmail}</span>
          ) : (
            <span className="text-xs italic text-text-muted">{t('admin.auditLog.noAdmin')}</span>
          ),
      },
      {
        id: 'action',
        accessor: (row) => row.action,
        header: t('admin.auditLog.columns.action'),
        sortable: true,
        // Ludzka nazwa akcji + surowy kod pod spodem.
        renderCell: (_value, row) => {
          const { label } = humanizeAuditAction(row.action, t);
          return (
            <div className="flex flex-col">
              <span className="text-sm text-text-primary">{label}</span>
              <span className="font-mono text-[11px] text-text-tertiary">{row.action}</span>
            </div>
          );
        },
      },
      {
        id: 'tenant',
        accessor: (row) =>
          `${row.tenantName ?? ''} ${row.tenantCode ?? ''} ${row.tenantSlug ?? ''}`,
        header: t('admin.auditLog.columns.tenant'),
        sortable: true,
        renderCell: (_value, row) =>
          row.tenantSlug ? (
            <div className="flex flex-col">
              <span className="text-sm text-text-primary">{row.tenantName}</span>
              <span className="font-mono text-xs text-text-tertiary">
                {row.tenantCode} · {row.tenantSlug}
              </span>
            </div>
          ) : (
            <span className="text-xs italic text-text-muted">{t('admin.auditLog.noTenant')}</span>
          ),
      },
      {
        id: 'details',
        accessor: (row) =>
          row.details !== null && row.details !== undefined ? JSON.stringify(row.details) : '',
        header: t('admin.auditLog.columns.details'),
        sortable: false,
        filterable: false,
        renderCell: (_value, row) =>
          row.details !== null && row.details !== undefined ? (
            <pre className="max-w-xs overflow-hidden truncate rounded bg-bg-tertiary px-2 py-1 font-mono text-[11px] text-text-secondary">
              {JSON.stringify(row.details)}
            </pre>
          ) : (
            <span className="text-xs text-text-muted">{t('admin.auditLog.noDetails')}</span>
          ),
      },
    ],
    [t],
  );

  const data = auditQuery.data;
  const rows = data?.entries ?? [];
  const emptyMessage = t('admin.auditLog.emptyNoData');

  return (
    <Layout title={t('admin.auditLog.title')}>
      <section className="px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">{t('admin.auditLog.title')}</h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.auditLog.subtitle')}</p>
        </div>

        {auditQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : auditQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.auditLog.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void auditQuery.refetch()}
            >
              {t('admin.auditLog.retry')}
            </Button>
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<AuditEntry>
                tableId="admin-audit-log"
                columns={columns}
                data={rows}
                getRowId={(row) => row.id}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={History}
                emptyTitle={emptyMessage}
                emptyDescription=""
              />
            </PreviewTenantAuthBoundary>
            {data && (
              <p className="mt-3 text-xs text-text-muted">
                {t('admin.auditLog.countLabel', { count: data.total })}
                {data.total > rows.length &&
                  ' ' +
                    t('admin.auditLog.countLabelPage', {
                      shown: rows.length,
                      total: data.total,
                    })}
              </p>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}
